/**
 * Componente Layout Inmersivo
 * Layout principal con scroll suave y efectos visuales premium
 * Estilo tipo Awwwards/Heinz
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SmoothScrollProvider } from '../../context/SmoothScrollContext';
import ImmersiveHeader from '../header/ImmersiveHeader';
import Footer from '../footer/Footer';
import CustomCursor from '../common/CustomCursor';
import { ScrollProgress } from '../animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Layout inmersivo con scroll suave
 * Incluye:
 * - Smooth scroll con Lenis
 * - Header transparente dinámico
 * - Cursor personalizado
 * - Barra de progreso de scroll
 * - Transiciones de página
 * 
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.children - Contenido de la página
 * @param {boolean} props.showFooter - Si debe mostrar el footer
 * @param {boolean} props.showProgress - Si debe mostrar la barra de progreso
 * @param {boolean} props.showCursor - Si debe mostrar el cursor personalizado
 * @param {string} props.className - Clases CSS adicionales
 * @returns {JSX.Element} Layout inmersivo
 */
const ImmersiveLayout = ({ 
  children, 
  showFooter = true,
  showProgress = true,
  showCursor = true,
  className = '',
}) => {
  const location = useLocation();

  // Reiniciar scroll y ScrollTrigger al cambiar de página
  useEffect(() => {
    // Scroll al inicio - múltiples métodos para asegurar compatibilidad
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Si Lenis está disponible, usarlo también
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    
    // Reforzar scroll después de que Lenis se inicialice completamente
    const scrollTimer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      }
    }, 50);
    
    // Refrescar ScrollTrigger después de un breve delay
    const refreshTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 150);

    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(refreshTimer);
    };
  }, [location.pathname]);

  // Animación de entrada de página
  useEffect(() => {
    const content = document.querySelector('.page-content');
    if (content) {
      gsap.fromTo(content,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }
      );
    }
  }, [location.pathname]);

  return (
    <SmoothScrollProvider>
      <div className={`min-h-screen bg-intercasa-bone ${className}`}>
        {/* Cursor personalizado (solo desktop) */}
        {showCursor && <CustomCursor />}
        
        {/* Barra de progreso de scroll */}
        {showProgress && <ScrollProgress />}
        
        {/* Header inmersivo */}
        <ImmersiveHeader />

        {/* Contenido principal */}
        <main className="page-content">
          {children}
        </main>

        {/* Footer */}
        {showFooter && <Footer />}
      </div>
    </SmoothScrollProvider>
  );
};

ImmersiveLayout.propTypes = {
  children: PropTypes.node.isRequired,
  showFooter: PropTypes.bool,
  showProgress: PropTypes.bool,
  showCursor: PropTypes.bool,
  className: PropTypes.string,
};

export default ImmersiveLayout;
